import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Receipt, Loader2, Check, Users } from 'lucide-react';
import { groupsAPI } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const GroupExpensePage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [splitType, setSplitType] = useState('equal');
  const [paidByUserId, setPaidByUserId] = useState('');
  const [customSplits, setCustomSplits] = useState({});

  useEffect(() => {
    fetchGroup();
  }, [groupId]);

  useEffect(() => {
    if (user?.id) {
      setPaidByUserId(user.id);
    }
  }, [user]);

  const fetchGroup = async () => {
    try {
      setLoading(true);
      const response = await groupsAPI.get(groupId);
      setGroup(response.data);
      
      // Initialize custom splits
      const splits = {};
      response.data.members?.forEach(m => {
        splits[m.user_id] = '';
      });
      setCustomSplits(splits);
    } catch (err) {
      setError('Couldn\'t load group');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = parseFloat(amount) || 0;
  const isValidAmount = totalAmount > 0;
  const memberCount = group?.members?.length || 0;
  const perPersonAmount = memberCount > 0 ? (totalAmount / memberCount).toFixed(2) : 0;

  const customTotal = Object.values(customSplits).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  const isCustomValid = splitType === 'custom' 
    ? Math.abs(customTotal - totalAmount) < 0.01
    : true;

  const canSubmit = isValidAmount && paidByUserId && (splitType === 'equal' || isCustomValid);

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) return;
    if (parts[1]?.length > 2) return;
    setAmount(value);
    setError('');

    // Reset custom splits when total changes
    if (splitType === 'custom') {
      const splits = {};
      group?.members?.forEach(m => {
        splits[m.user_id] = '';
      });
      setCustomSplits(splits);
    }
  };

  const handleCustomSplitChange = (userId, value) => {
    const numValue = value.replace(/[^\d.]/g, '');
    setCustomSplits(prev => ({
      ...prev,
      [userId]: numValue,
    }));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');

    try {
      const data = {
        total_amount: totalAmount,
        description: description || 'Group expense',
        split_type: splitType,
        paid_by_user_id: paidByUserId,
      };

      if (splitType === 'custom') {
        const validSplits = {};
        Object.entries(customSplits).forEach(([userId, value]) => {
          if (parseFloat(value) > 0) {
            validSplits[userId] = parseFloat(value);
          }
        });
        data.custom_splits = validSplits;
      }

      await groupsAPI.createExpense(groupId, data);
      toast.success('Expense added!');
      navigate(`/groups/${groupId}`);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to create expense';
      setError(typeof message === 'string' ? message : 'Failed to create expense');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (num) => {
    if (!num || isNaN(num)) return '₹0';
    return `₹${parseFloat(num).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <p className="text-lg font-medium mb-4">{error}</p>
        <button onClick={() => navigate(`/groups/${groupId}`)} className="btn-secondary">
          Back to Group
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-6 py-4 flex items-center gap-4 border-b-2 border-black">
        <button 
          onClick={() => navigate(`/groups/${groupId}`)}
          className="hover:opacity-70 transition-opacity"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-xl font-heading font-bold">Add Expense</h1>
          <p className="text-xs text-neutral-500">{group?.name}</p>
        </div>
      </header>

      <main className="flex-1 p-6 pb-32 space-y-6">
        {/* Amount Input */}
        <div className="text-center">
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide">
            Total Amount
          </label>
          <div className="relative mt-2">
            <span className="absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-[calc(50%+2ch)] text-5xl font-heading font-bold text-neutral-300">
              ₹
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={handleAmountChange}
              placeholder="0"
              className="input-amount w-full"
              autoFocus
              data-testid="amount-input"
            />
          </div>
        </div>

        {/* Paid By Selector */}
        <div>
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide block mb-2">
            Paid By
          </label>
          <div className="grid grid-cols-2 gap-2">
            {group?.members?.map((member, index) => (
              <button
                key={member.user_id}
                onClick={() => setPaidByUserId(member.user_id)}
                className={`p-3 rounded-xl border-2 font-medium transition-all flex items-center gap-2 ${
                  paidByUserId === member.user_id
                    ? 'border-black bg-gradient-to-r from-violet-500 to-purple-600 text-white'
                    : 'border-black bg-white text-black hover:bg-neutral-50'
                }`}
                data-testid={`paid-by-${member.user_id}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  paidByUserId === member.user_id
                    ? 'bg-white/20 text-white'
                    : `bg-gradient-to-br ${
                        index === 0 ? 'from-amber-400 to-orange-500' :
                        index === 1 ? 'from-pink-500 to-rose-500' :
                        index === 2 ? 'from-cyan-500 to-blue-500' :
                        'from-emerald-500 to-teal-500'
                      } text-white`
                }`}>
                  {member.user_name?.[0]?.toUpperCase() || '?'}
                </div>
                <span className="truncate text-sm">
                  {member.user_id === user?.id ? 'You' : member.user_name || 'Unknown'}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Split Type */}
        <div>
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide block mb-2">
            Split Type
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setSplitType('equal')}
              className={`py-4 px-4 rounded-xl border-2 font-bold transition-all ${
                splitType === 'equal'
                  ? 'border-black bg-black text-white'
                  : 'border-black bg-white text-black hover:bg-neutral-100'
              }`}
              data-testid="split-type-equal"
            >
              Equal Split
            </button>
            <button
              onClick={() => setSplitType('custom')}
              className={`py-4 px-4 rounded-xl border-2 font-bold transition-all ${
                splitType === 'custom'
                  ? 'border-black bg-black text-white'
                  : 'border-black bg-white text-black hover:bg-neutral-100'
              }`}
              data-testid="split-type-custom"
            >
              Custom
            </button>
          </div>
        </div>

        {/* Split Preview / Custom Inputs */}
        <div className="card-base">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-neutral-500" />
            <span className="font-bold">Split Breakdown</span>
          </div>
          
          {splitType === 'equal' ? (
            <div className="space-y-3">
              {group?.members?.map((member, index) => (
                <div key={member.user_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-gradient-to-br ${
                      index === 0 ? 'from-amber-400 to-orange-500' :
                      index === 1 ? 'from-pink-500 to-rose-500' :
                      index === 2 ? 'from-cyan-500 to-blue-500' :
                      'from-emerald-500 to-teal-500'
                    }`}>
                      {member.user_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <span className="text-neutral-700">
                      {member.user_id === user?.id ? 'You' : member.user_name || 'Unknown'}
                    </span>
                  </div>
                  <span className={`font-bold ${
                    member.user_id === paidByUserId ? 'text-neutral-400 line-through' : ''
                  }`}>
                    {formatCurrency(perPersonAmount)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {group?.members?.map((member, index) => (
                <div key={member.user_id} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-gradient-to-br ${
                    index === 0 ? 'from-amber-400 to-orange-500' :
                    index === 1 ? 'from-pink-500 to-rose-500' :
                    index === 2 ? 'from-cyan-500 to-blue-500' :
                    'from-emerald-500 to-teal-500'
                  }`}>
                    {member.user_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <span className="text-neutral-700 w-20 truncate text-sm">
                    {member.user_id === user?.id ? 'You' : member.user_name || 'Unknown'}
                  </span>
                  <div className="flex-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">₹</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={customSplits[member.user_id] || ''}
                      onChange={(e) => handleCustomSplitChange(member.user_id, e.target.value)}
                      placeholder="0"
                      className="input-base pl-7 h-10 text-sm"
                      disabled={member.user_id === paidByUserId}
                      data-testid={`custom-split-${member.user_id}`}
                    />
                  </div>
                </div>
              ))}
              
              <div className={`flex items-center justify-between pt-3 border-t-2 border-dashed ${
                isCustomValid ? 'border-green-300' : 'border-red-300'
              }`}>
                <span className="text-neutral-600">Total</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{formatCurrency(customTotal)} / {formatCurrency(totalAmount)}</span>
                  {isCustomValid ? (
                    <Check className="w-5 h-5 text-success" />
                  ) : (
                    <span className="text-error text-sm">Doesn't match</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide block mb-2">
            Description
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Dinner, Uber, Hotel, etc."
            className="input-base"
            data-testid="description-input"
          />
        </div>

        {error && (
          <p className="text-error text-sm font-medium text-center" data-testid="error-message">
            {error}
          </p>
        )}
      </main>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-white border-t-2 border-black">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="create-expense-button"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              Add Expense
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default GroupExpensePage;
